import { PageCard, type PageCardStyle } from "./PageCard";
import { workCover, workCardText } from "./workDetails";
import { pageDisplayLabels } from "../document/blockStyles";
import { useContext, useEffect, useState, type ReactNode } from "react";
import { PageThumbnails } from "./pageThumbnails";
import { parseBlockContent } from "../../../lib/blockContentSchemas";
import type { BlockContent } from "../../../lib/blockContentSchemas";
import type {
  BlockEditPatch,
  EditorAsset,
  EditorBlock,
  EditorPage,
} from "../editor.types";
import { blockService } from "../../blocks";
import { assetService } from "../../assets";
import { EditActions, EditField } from "./BlockEditControls";
import { editInputClass, onEscapeCancel } from "../editUtils";

type PageBlockProps = {
  block: EditorBlock;
  pages: EditorPage[];
  assetsById?: Map<string, EditorAsset>;
  currentPageId?: string;
  editing?: boolean;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancelEdit?: () => void;
  href?: string;
  onOpen?: () => void;
  /** Set by a library, whose style applies to all its cards. */
  display?: PageCardStyle;
  compact?: boolean;
};

export const PageBlock = ({
  block,
  pages,
  currentPageId,
  editing,
  onSave,
  onCancelEdit,
  href,
  onOpen,
  assetsById,
  display,
  compact,
}: PageBlockProps): ReactNode => {
  const content = parseBlockContent("page", block.content);
  const target = pages.find(
    (page) => page.source_page_id === block.target_page_source_id,
  );

  const [fetched, setFetched] = useState<EditorBlock | null>(null);
  const targetId = target?.id;
  const cached = useContext(PageThumbnails);
  const first = cached && targetId ? cached(targetId) : fetched;
  const picture = target ? workCover(target, first?.page_id === targetId ? first : null) : null;
  useEffect(() => {
    let active = true;
    if (!cached && targetId && /^[0-9a-f-]{36}$/i.test(targetId))
      void blockService
        .firstPicture(targetId)
        .then((p) => {
          if (active) setFetched(p);
        })
        .catch(() => {
          if (active) setFetched(null);
        });
    return () => {
      active = false;
    };
  }, [targetId, cached]);
  const thumbnail =
    picture?.asset_id
      ? assetsById?.get(picture.asset_id)
      : undefined;

  if (editing) {
    return (
      <PageBlockEditor
        block={block}
        content={content}
        currentPageId={currentPageId}
        onCancel={onCancelEdit}
        onSave={onSave}
        pages={pages}
      />
    );
  }

  if (!target) return null;

  const cardText = workCardText(target);
  return (
    <PageCard
      variant={display ?? content.display}
      compact={compact}
      title={target.title}
      details={cardText.details}
      description={content.showDescription ? cardText.description : null}
      imageUrl={thumbnail ? assetService.getPublicUrl(thumbnail.storage_path) : null}
      sample={(picture?.content as { sample?: string } | undefined)?.sample}
      href={href}
      onOpen={onOpen}
    />
  );
};

type PageBlockEditorProps = {
  block: EditorBlock;
  content: BlockContent<"page">;
  pages: EditorPage[];
  currentPageId?: string;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancel?: () => void;
};

const PageBlockEditor = ({
  block,
  content,
  pages,
  currentPageId,
  onSave,
  onCancel,
}: PageBlockEditorProps) => {
  const targetOptions = pages.filter((page) => page.id !== currentPageId);
  const [draftTarget, setDraftTarget] = useState(
    block.target_page_source_id ?? targetOptions[0]?.source_page_id ?? "",
  );
  const [draftDisplay, setDraftDisplay] = useState(content.display);
  const [draftShowDescription, setDraftShowDescription] = useState(
    content.showDescription,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    const result = await onSave?.({
      content: { display: draftDisplay, showDescription: draftShowDescription },
      target_page_source_id: draftTarget,
    });
    setSaving(false);
    if (result) setError(result);
  };

  return (
    <div
      className="space-y-3 rounded-2xl border border-hazumu-border bg-hazumu-warm-white p-4 text-hazumu-ink scheme-light"
      onKeyDown={onEscapeCancel(onCancel)}
    >
      <EditField label="Page">
        <select
          autoFocus
          className={editInputClass}
          onChange={(event) => setDraftTarget(event.target.value)}
          value={draftTarget}
        >
          {targetOptions.map((page) => (
            <option key={page.id} value={page.source_page_id}>
              {page.title}
            </option>
          ))}
        </select>
      </EditField>
      <EditField label="Display">
        <select
          className={editInputClass}
          onChange={(event) =>
            setDraftDisplay(
              event.target.value as BlockContent<"page">["display"],
            )
          }
          value={draftDisplay}
        >
          {Object.entries(pageDisplayLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </EditField>
      <label className="flex items-center gap-2 text-sm font-semibold text-hazumu-ink">
        <input
          checked={draftShowDescription}
          className="size-4 accent-hazumu-terracotta"
          onChange={(event) => setDraftShowDescription(event.target.checked)}
          type="checkbox"
        />
        Show page description
      </label>
      <EditActions
        error={error}
        onCancel={onCancel}
        onSave={save}
        saveDisabled={!draftTarget}
        saving={saving}
      />
    </div>
  );
};
